import React from "react";
import {
  fmtCcy,
  fmtPrice,
  fmtUnits,
  formatFriendly,
  formatFriendlyTime,
  signedCcy,
  signedUnits,
} from "../helpers";
import {
  ApiEntryDrift,
  ApiPaymentDriftChange,
  ApiSupersededEntry,
  ApiTransactionDrift,
  ReconRow,
} from "../types";
import Icon from "../Icon";
import { CHANGE_LABELS, describeChange } from "../paymentDriftChange";

const DASH = "—";

const NOTE: React.CSSProperties = {
  fontSize: 12.5,
  color: "var(--ink-2)",
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  padding: "10px 12px",
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
};

// One run in the history, current or superseded, reduced to the figures the
// comparison table shows so both sources render through the same path.
interface RunColumn {
  key: string;
  label: string;
  when: string;
  reversedAt: string | null;
  isCurrent: boolean;
  price: string;
  paymentsAmount: string;
  paymentsUnits: string;
  matchStatus: string;
  amountDifference: string;
  unitDifference: string;
  attribution: string;
  unitMismatch: string;
  drift: ApiTransactionDrift | null;
}

function fromSuperseded(entry: ApiSupersededEntry): RunColumn {
  return {
    key: entry.id,
    label: formatFriendly(entry.createdAt, true),
    when: entry.createdAt,
    reversedAt: entry.reversedAt,
    isCurrent: false,
    price: entry.priceAmount != null ? fmtPrice(entry.priceAmount) : DASH,
    paymentsAmount:
      entry.paymentsAmount != null ? fmtCcy(entry.paymentsAmount) : DASH,
    paymentsUnits:
      entry.paymentsUnits != null ? fmtUnits(entry.paymentsUnits) : DASH,
    amountDifference:
      entry.diffAmount != null ? signedCcy(entry.diffAmount) : DASH,
    unitDifference:
      entry.diffUnits != null ? signedUnits(entry.diffUnits) : DASH,
    matchStatus: entry.matchStatus,
    attribution: `${entry.matchedPaymentCount} / ${entry.missedPaymentCount}`,
    unitMismatch:
      entry.unitDiscrepancies > 0 ? String(entry.unitDiscrepancies) : DASH,
    drift: entry.transactionDrift,
  };
}

function fromCurrent(row: ReconRow, drift: ApiTransactionDrift): RunColumn {
  return {
    key: "current",
    label: row.created
      ? `Current · ${formatFriendly(row.created, true)}`
      : "Current",
    when: row.created,
    reversedAt: null,
    isCurrent: true,
    price: fmtPrice(row.price),
    paymentsAmount: fmtCcy(row.amountTA),
    paymentsUnits: fmtUnits(row.unitsTA),
    amountDifference: signedCcy(row.diff ?? 0),
    unitDifference: signedUnits(row.diffUnits ?? 0),
    matchStatus: row.match,
    attribution: `${row.matchedPaymentCount} / ${row.missedPaymentCount}`,
    unitMismatch: row.unitDiscrepancies ? String(row.unitDiscrepancies) : DASH,
    drift,
  };
}

// Runs are the unbounded axis - a product can be rerun any number of times -
// so they run down the table while the fixed set of metrics runs across it.
// The reverse put a ceiling of about five runs before the metric labels
// scrolled out of view.
const METRICS: {
  label: string;
  read: (run: RunColumn) => string;
  width: number;
}[] = [
  { label: "Status", read: (r) => r.matchStatus, width: 104 },
  { label: "Unit price", read: (r) => r.price, width: 96 },
  { label: "Payments amount", read: (r) => r.paymentsAmount, width: 132 },
  { label: "Payments units", read: (r) => r.paymentsUnits, width: 156 },
  { label: "Amount difference", read: (r) => r.amountDifference, width: 116 },
  { label: "Unit difference", read: (r) => r.unitDifference, width: 124 },
  { label: "Matched / missed", read: (r) => r.attribution, width: 116 },
  { label: "Unit mismatch", read: (r) => r.unitMismatch, width: 104 },
];

const HEAD_CELL: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--surface-2)",
  color: "var(--muted)",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 600,
  whiteSpace: "nowrap",
  textAlign: "right",
};

// Kept visible while the metric columns scroll, so a row is always identifiable.
const STICKY: React.CSSProperties = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  textAlign: "left",
};

function ComparisonTable({ runs }: { runs: RunColumn[] }) {
  return (
    <div className="narr-tbl" style={{ marginBottom: 24 }}>
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}
      >
        <thead>
          <tr>
            <th style={{ ...HEAD_CELL, ...STICKY, zIndex: 2 }}>Run</th>
            {METRICS.map((metric) => (
              <th
                key={metric.label}
                style={{ ...HEAD_CELL, minWidth: metric.width }}
              >
                {metric.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run, rowIndex) => {
            const previous = rowIndex > 0 ? runs[rowIndex - 1] : null;
            return (
              <tr key={run.key}>
                <td
                  style={{
                    ...STICKY,
                    padding: "9px 14px",
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    whiteSpace: "nowrap",
                    fontWeight: run.isCurrent ? 700 : 500,
                    color: run.isCurrent ? "var(--ink)" : "var(--ink-2)",
                  }}
                  title={formatFriendly(run.when, true)}
                >
                  {run.label}
                </td>
                {METRICS.map((metric) => {
                  const value = metric.read(run);
                  const moved =
                    previous != null && metric.read(previous) !== value;
                  return (
                    <td
                      key={metric.label}
                      className="mono"
                      style={{
                        padding: "9px 14px",
                        borderTop: "1px solid var(--border)",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        color:
                          value === "mismatched"
                            ? "var(--danger)"
                            : moved
                            ? "var(--ink)"
                            : undefined,
                        fontWeight:
                          moved || value === "mismatched" ? 600 : undefined,
                      }}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ChangeList({ changes }: { changes: ApiPaymentDriftChange[] }) {
  return (
    <>
      {changes.map((change) => {
        const { before, after } = describeChange(change);

        return (
          <div
            key={`${change.paymentId}-${change.change}`}
            style={{
              display: "flex",
              alignItems: "baseline",
              flexWrap: "wrap",
              gap: 12,
              padding: "9px 0 9px 14px",
              borderLeft: "2px solid var(--danger)",
              marginBottom: 8,
              fontSize: 12.5,
            }}
          >
            <span className="mono" style={{ whiteSpace: "nowrap" }}>
              {change.paymentTaId
                ? `TA ${change.paymentTaId}`
                : `#${change.paymentId ?? DASH}`}
            </span>
            <span
              style={{
                color: "var(--danger)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              {CHANGE_LABELS[change.change] ?? change.change}
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 6,
                minWidth: 0,
              }}
            >
              <span style={{ color: "var(--muted)" }}>{before}</span>
              <Icon name="chevron-right" size={11} strokeWidth={2.4} />
              <span style={{ fontWeight: 600 }}>{after}</span>
            </span>
          </div>
        );
      })}
    </>
  );
}

function Step({ run }: { run: RunColumn }) {
  const drift = run.drift;

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        className="section-label"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span>
          {run.isCurrent ? "Latest rerun" : "Rerun"} ·{" "}
          {formatFriendly(run.when, true)}
        </span>
        {!run.isCurrent && run.reversedAt && (
          <span style={{ color: "var(--muted)", fontWeight: 500 }}>
            superseded {formatFriendlyTime(run.reversedAt)}
          </span>
        )}
      </div>

      {drift?.price?.changed && (
        <div style={{ ...NOTE, marginBottom: 10 }}>
          <Icon name="alert-triangle" size={13} strokeWidth={2.2} />
          <span>
            <strong style={{ color: "var(--ink-2)" }}>
              Unit price restated
            </strong>{" "}
            from{" "}
            {drift.price.previous != null
              ? fmtPrice(drift.price.previous)
              : DASH}{" "}
            to{" "}
            {drift.price.current != null ? fmtPrice(drift.price.current) : DASH}
            . Every transaction&rsquo;s units on this value date derive from it.
          </span>
        </div>
      )}

      {drift && drift.changes.length > 0 && (
        <ChangeList changes={drift.changes} />
      )}

      {drift && !drift.comparable && (
        <div style={NOTE}>
          <Icon name="info" size={13} strokeWidth={2.2} />
          <span>
            The run this replaced predates transaction snapshotting, so what
            moved can&rsquo;t be named.
          </span>
        </div>
      )}

      {drift &&
        drift.comparable &&
        drift.changes.length === 0 &&
        !drift.price?.changed && (
          <div
            style={{ color: "var(--muted)", fontSize: 12.5, paddingLeft: 2 }}
          >
            No payment changed in this rerun.
          </div>
        )}
    </div>
  );
}

const SupersededTab: React.FC<{
  row: ReconRow;
  drift: ApiEntryDrift | null;
  error: string | null;
}> = ({ row, drift, error }) => {
  if (error) {
    return (
      <div style={{ color: "var(--danger)", fontSize: 13, padding: "10px 0" }}>
        {error}
      </div>
    );
  }
  if (!drift) {
    return (
      <div style={{ color: "var(--muted)", fontSize: 13, padding: "10px 0" }}>
        Loading rerun history…
      </div>
    );
  }

  const { supersededEntries } = drift;

  // supersededEntries arrives newest first; the table reads left to right as a
  // progression, so oldest first with the live entry last.
  const runs: RunColumn[] = [
    ...[...supersededEntries].reverse().map(fromSuperseded),
    fromCurrent(row, drift.transactionDrift),
  ];

  // Each run's own drift records what that rerun changed, so the log is the
  // whole history rather than only the most recent step. The oldest run is
  // dropped: it has no predecessor here to have changed anything relative to.
  const steps = [...runs].reverse().slice(0, -1);

  return (
    <>
      <div className="section-label" style={{ marginTop: 10 }}>
        Rerun history &mdash; {supersededEntries.length}{" "}
        {supersededEntries.length === 1 ? "earlier run" : "earlier runs"}
      </div>

      <ComparisonTable runs={runs} />

      <div className="section-label">What changed, newest first</div>

      {steps.map((run) => (
        <Step key={run.key} run={run} />
      ))}
    </>
  );
};

export default SupersededTab;
